import styled from 'styled-components';
import { PageContainer } from '../../../components/PageContainer';
import { useEffect, useState } from 'react';
import { CodeType } from '../components/CodeType';
import { EnterCode } from '../components/EnterCode';
import { ChooseSubscription } from '../components/ChooseSubscription';
import { EnterEmail } from '../components/EnterEmail';
import { useGetUserDataQuery } from '../../auth/api/authApi';
import { useSelector } from 'react-redux';
import { authSelector } from '../../auth/state/authSlice';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SelectLocation } from '../components/SelectLocation';
import { EsimUpdateBanner } from '../components/EsimUpdateNotice';
import { useTrackReferralMutation } from '../api/activateApi';
import { toast } from 'react-toastify';
import { Button } from '../../../components/Button';

export type LocationsOptions = 'uk' | 'ie';

export const Activate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useSelector(authSelector);
  const { data: userData } = useGetUserDataQuery({ token: token || '' }, { skip: !token });
  const [indexStep, setIndexStep] = useState(0);

  const [codeType, setCodeType] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [iccid, setIccid] = useState<string>('');
  const [location, setLocation] = useState<LocationsOptions>('uk');
  const [esimManualUpdate, setEsimManualUpdate] = useState<string>('');
  const [reservationId, setReservationId] = useState<string>('');

  const [email, setEmail] = useState<string>(userData?.data.email || '');

  // Affiliate referral code (?ref=CODE). Persisted so it survives the multi-step
  // flow even though intermediate steps rewrite the query string.
  const [refCode, setRefCode] = useState<string>('');
  const [trackReferral] = useTrackReferralMutation();

  useEffect(() => {
    const refParam = searchParams.get('ref') || localStorage.getItem('trade_ref') || '';
    if (refParam) {
      setRefCode(refParam);
      localStorage.setItem('trade_ref', refParam);
    }
  }, []);

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (codeType === '20-digits') {
      if (e.target.value.length > 20) return;
      if (isNaN(Number(e.target.value))) return;
      setIccid(e.target.value);
      setReservationId('');
    }
    if (codeType === '4-digits') {
      if (e.target.value.length > 4) return;
      const alphaRegex = /^[a-zA-Z]+$/;
      if (!alphaRegex.test(e.target.value) && e.target.value != '') return;
      setCode(e.target.value.toUpperCase());
      setReservationId('');
    }
  };

  const onCodeTypeSwitch = () => {
    const newCodeType = codeType === '20-digits' ? '4-digits' : '20-digits';
    setCodeType(newCodeType);
    setCode('');
    setIccid('');
    setReservationId('');
    setSearchParams({ email, code_type: newCodeType });
  };

  const onMissingInfo = () => {
    if (!email) {
      setIndexStep(0);
      toast('You must enter an email', { type: 'error' });
      return;
    }
    if (!iccid) {
      setIndexStep(2);
      toast('You must enter a code', { type: 'error' });
      return;
    }
  };

  const steps = [
    {
      title: 'Enter e-mail',
      content: (
        <>
          <WelcomeText>
            <h2>Streaming Plan Activation</h2>
            <p>
              Please enter the Email that you would like to be associated with your Streaming plan. If you're activating more than 1 SIM please ensure you use the same email for each.
            </p>
            {process.env.REACT_APP_GROUP_ACTIVATION_LINK_ENABLED === 'true' && (
              <p style={{ marginTop: '1rem' }}>
                <a href="/activate/group" style={{ color: '#126b89', textDecoration: 'underline' }}>
                  Activating more than one solar SIM? Set them up together →
                </a>
              </p>
            )}
          </WelcomeText>
          <EnterEmail
            onClick={() => {
              if (refCode && email) {
                // Fire-and-forget attribution; must not block activation.
                trackReferral({ ref: refCode, email, iccid: iccid || undefined });
              }
              if (code || codeType) {
                setIndexStep(2);
              } else {
                setIndexStep(1);
              }
              setSearchParams({ email, ...(refCode ? { ref: refCode } : {}) });
            }}
            email={email}
            setEmail={setEmail}
            isActive={Boolean(email)}
          />
        </>
      )
    },
    {
      title: 'Enter code type',
      content: (
        <CodeType
          onClick={() => {
            setIndexStep(2);
            setSearchParams({ email, code_type: codeType });
          }}
          codeType={codeType}
          setCodeType={setCodeType}
          isActive={Boolean(codeType)}
          onGoBack={() => {
            setIndexStep(0);
            setCodeType('');
          }}
        />
      )
    },
    {
      title: 'Enter code',
      content: (
        <EnterCode
          codeType={codeType}
          code={code}
          isActive={Boolean(code || iccid)}
          onCodeChange={onCodeChange}
          onCodeTypeSwitch={onCodeTypeSwitch}
          onClick={(esimBatchLabel?: string) => {
            const batchLabel = esimBatchLabel || '';
            setEsimManualUpdate(batchLabel);
            if (batchLabel) {
              localStorage.setItem('esim_manual_update', batchLabel);
            } else {
              localStorage.removeItem('esim_manual_update');
            }
            setIndexStep(3);
            setSearchParams({ email, code_type: codeType, code });
          }}
          iccid={iccid}
          setIccid={setIccid}
          setReservationId={setReservationId}
          onGoBack={() => {
            setIndexStep(1);
            setCode('');
            setIccid('');
          }}
        />
      )
    },
    {
      title: 'Select Region',
      content: (
        <>
          {esimManualUpdate && <EsimUpdateBanner batchLabel={esimManualUpdate} />}
          <SelectLocation
            location={location}
            setLocation={setLocation}
            onGoBack={() => {
              setIndexStep(2);
            }}
            onClick={() => {
              setSearchParams({ email, code_type: codeType, code, location });
              setTimeout(() => setIndexStep(4), 100); //TODO: debug issue with location not being set
            }}
          />
        </>
      )
    },
    {
      title: 'Choose plan',
      content: (
        <>
          {esimManualUpdate && <EsimUpdateBanner batchLabel={esimManualUpdate} />}
          <ChooseSubscription
            email={email}
            iccid={iccid}
            reservationId={reservationId}
            onGoBack={() => setIndexStep(3)}
            onMissingInfo={onMissingInfo}
            location={location}
          />
        </>
      ),
      onClick: () => {}
    }
  ];

  useEffect(() => {
    const email = searchParams.get('email');
    const codeType = searchParams.get('code_type');
    const code = searchParams.get('code');

    if (email) {
      setEmail(email);
    }
    if (codeType) {
      setCodeType(codeType);
    }
    if (code) {
      setCode(code);
    }
  }, []);

  const titles = steps.map((step) => step.title);
  return (
    <PageContainer title="Activate">
      <LoginButtonWrapper>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Login to existing account
        </Button>
      </LoginButtonWrapper>
      <Container>
        <StepsContainer>
          {titles.map((title, index) => (
            <StepWrapper key={title} isActive={index === indexStep}>
              <Circle isFirst={index === 0} isLast={index + 1 === titles.length}>
                {index + 1}
              </Circle>
              <StepTitle isActive={index === indexStep}>{title}</StepTitle>
            </StepWrapper>
          ))}
        </StepsContainer>
        <Board>{steps[indexStep].content}</Board>
      </Container>
    </PageContainer>
  );
};

const LoginButtonWrapper = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 10;

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
  }
`;

const Board = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  background-color: ${({ theme }) => theme.colors.gray};
  margin-top: 2rem;

  @media (max-width: 850px) {
    padding: 0.5rem;
  }
`;

const Container = styled.div`
  border-radius: 0.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  position: relative;
  width: 32rem;

  @media (max-width: 850px) {
    width: 80%;
  }
`;

const StepWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  transform: ${({ isActive }) => (isActive ? 'scale(1.2)' : 'scale(1)')};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
`;

const Circle = styled.div<{ isFirst?: boolean; isLast?: boolean }>`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.8rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;
const StepTitle = styled.p<{ isActive: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  position: absolute;
  color: ${({ theme }) => theme.colors.primary};
  bottom: -1.2rem;
  width: 8rem;
  text-align: center;

  @media (max-width: 850px) {
    ${({ isActive }) => !isActive && 'display: none;'}
  }
`;

const WelcomeText = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 500px;

  h2 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 1rem;
    font-size: ${({ theme }) => theme.fontSize.medium};
  }

  p {
    color: ${({ theme }) => theme.colors.gray900};
    font-size: ${({ theme }) => theme.fontSize.small};
    line-height: 1.5;
  }
`;
